import * as React from 'react';
import { useAppNavigation } from "../../hooks/useAppNavigation";
import SubPages from "../../pages/subpages/SubPagesEnum";
import { MyModelsPageState } from '../../pages/subpages/MyModels';
import { useState, useEffect, useReducer } from 'react';

import { MyModelMetadata } from "../../pages/subpages/MyModels";
import apiService from '../../services/ApiService';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import "../style/ModelFolder.css"
import ModelListItemOptionMenu from './ModelListItemOptionMenu';
import { useAppStore } from '../../shared/stores/useAppStore';
import TailwindModal from '../Tailwind/TailwindModal';
import TailwindButton from '../Tailwind/TailwindButton';
import { cn } from '../../lib/utils';

const deleteModal = (model: MyModelMetadata, state: MyModelsPageState, reloadState: Function) => {
    const parentElem = state.folders[model.folderId.toString()]

    const handleClose = () => {
        model.deleteModalOpen = false;
        reloadState();
    }

    const handleDelete = () => {
        apiService.deleteModel(model.id).then(response => {
            if (response) {
                model.deleteModalOpen = false;
                const index = parentElem.modelChildrensIds.indexOf(model.id);
                if (index !== -1) {
                    parentElem.modelChildrensIds.splice(index, 1);
                }
                delete state.models[model.id.toString()]
                useAppStore.getState().incrementModelsRemaining();
                reloadState();
            }
        })
    }

    return (
        <TailwindModal
            isOpen={model.deleteModalOpen}
            onClose={handleClose}
            title="Are you sure?"
            size="sm"
            footer={
                <div className="flex justify-center gap-3 w-full">
                    <TailwindButton variant="ghost" icon="close" size="sm" onClick={handleClose}>
                        Cancel
                    </TailwindButton>
                    <TailwindButton variant="danger" icon="delete_forever" size="sm" onClick={handleDelete}>
                        Yes, Delete
                    </TailwindButton>
                </div>
            }
        >
            <p className="text-base text-center text-slate-700">
                This will delete <strong>{model.name}</strong> and all its content permanently.
            </p>
        </TailwindModal>
    );
}

const leaveModal = (model: MyModelMetadata, state: MyModelsPageState, reloadState: Function) => {
    const handleClose = () => {
        model.deleteModalOpen = false;
        reloadState();
    }

    const handleLeave = () => {
        apiService.leaveSharedModels(model.id).then(async response => {
            if (response) {
                if (response.ok) {
                    const folderSharedModelId = await response.json()
                    const parentElem = state.folders[folderSharedModelId.toString()]
                    model.deleteModalOpen = false;
                    const index = parentElem.modelChildrensIds.indexOf(model.id);
                    if (index !== -1) {
                        parentElem.modelChildrensIds.splice(index, 1);
                    }
                    delete state.models[model.id.toString()]
                    reloadState();
                }
            }
        })
    }

    return (
        <TailwindModal
            isOpen={model.deleteModalOpen}
            onClose={handleClose}
            title="Are you sure?"
            size="sm"
            footer={
                <div className="flex justify-center gap-3 w-full">
                    <TailwindButton variant="ghost" icon="close" size="sm" onClick={handleClose}>
                        Cancel
                    </TailwindButton>
                    <TailwindButton variant="danger" icon="exit_to_app" size="sm" onClick={handleLeave}>
                        Yes, leave sharing
                    </TailwindButton>
                </div>
            }
        >
            <p className="text-base text-center text-slate-700">
                This will delete <strong>{model.name}</strong> from the shared models.
            </p>
        </TailwindModal>
    );
}

function RenameForm({ model, reloadState, isSharedContext }: { model: MyModelMetadata, reloadState: Function, isSharedContext: boolean }) {
    const [name, setName] = useState(model.name);
    const [error, setError] = useState(false);

    const onSubmit = (event) => {
        event.preventDefault();
        if (!name.trim()) {
            setError(true);
            return;
        }
        if (model.id) {
            const putApi = isSharedContext
                ? apiService.putModelSharedModels(model.id, model.folderId)
                : apiService.putModel(model.id, name, model.folderId);

            putApi.then(response => {
                if (response) {
                    if (!isSharedContext) model.name = name;
                    model.editing = false;
                    reloadState();
                }
            });
        }
    };

    return (
        <div className="flex-1 min-w-0">
            <form onSubmit={onSubmit}>
                <div className="relative flex items-center h-10 gap-1">
                    <input
                        type="text"
                        value={name}
                        className={cn(
                            "flex-1 min-w-0 ml-1.5 border-b focus:border-primary outline-none px-1 py-1 text-sm bg-transparent",
                            error ? "border-red-400" : "border-slate-300"
                        )}
                        onChange={(e) => { setName(e.target.value); if (error) setError(false); }}
                        onFocus={event => { event.target.select(); }}
                        autoFocus
                    />
                    <button
                        type="submit"
                        className="size-8 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-center text-slate-500"
                        aria-label="Confirm rename"
                    >
                        <span className="material-symbols-outlined text-[20px]">done</span>
                    </button>
                    <button
                        type="button"
                        className="size-8 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-center text-slate-500"
                        onClick={() => { model.editing = false; reloadState(); }}
                        aria-label="Cancel rename"
                    >
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                    {error && (
                        <p className="absolute bottom-full left-0 mb-1 text-xs text-red-500 bg-white border border-red-100 rounded px-1.5 py-0.5 shadow-sm whitespace-nowrap">
                            Name cannot be empty
                        </p>
                    )}
                </div>
            </form>
        </div>
    );
}

export default function ModelListItem({ model, state, reloadState, isSharedContext = false, stateMyModels = null }) {
    const navigateToPage = useAppNavigation();
    const [{ isDragging }, drag, preview] = useDrag(() => ({
        type: 'ELEM',
        collect: (monitor) => ({
          isDragging: !!monitor.isDragging(),
        }),
        item: { ...model, isSharedContext, type: 'MODEL' }
      }),
       [model, isSharedContext]
      )

    useEffect(() => {
        preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [, forceRender] = useReducer((x: number) => x + 1, 0);

    const [userRights, setUserRights] = useState({
        user_id: 0,
        right_to_share: false,
        right_to_read: false,
        right_to_write: false,
        right_to_duplicate: false,
    })

    useEffect(() => {
        if (isSharedContext) {
            apiService.getUserRightsSharedModels(model.id).then(async response => {
                if (response) {
                    if (response.ok) {
                        const elem = await response.json()
                        setUserRights({
                            user_id: elem.user_id,
                            right_to_share: elem.right_to_share,
                            right_to_read: elem.right_to_read,
                            right_to_write: elem.right_to_write,
                            right_to_duplicate: elem.right_to_duplicate,
                        })
                    }
                }
            })
        }
    }, []);

    return (
        <div ref={(model.editing) ? null : drag} style={{ cursor: 'move', opacity: isDragging ? 0.5 : 1 }}>
            <div className="min-w-[250px] h-12 flex justify-end">
                <div
                    className="block text-left w-full h-full border-none bg-white cursor-default"
                    onDoubleClick={() => {
                        if (!model.editing && (!isSharedContext || userRights.right_to_read) && model.modelRulesDeducted?.is_active !== false) {
                            if (model.isNew) {
                                navigateToPage(SubPages.NewModel, model);
                            } else {
                                navigateToPage(SubPages.FinancialModelEditor, model);
                            }
                        }
                    }}
                >
                    <div className={cn(
                        "models-item-component group flex w-full h-full items-center border-none hover:bg-slate-50 transition-colors"
                    )}>
                        <span className="material-symbols-outlined text-[22px] ml-6 text-slate-500">
                            architecture
                        </span>
                        {
                            model.editing ?
                            <RenameForm model={model} reloadState={reloadState} isSharedContext={isSharedContext} /> :
                            <p className="truncate ml-3 text-sm font-medium text-slate-800">{model.name}</p>
                        }
                        {isSharedContext ? (
                            <span className="inline-flex items-center ml-2 gap-1 text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded shrink-0">
                                <span className="material-symbols-outlined text-[12px]">group</span>
                                Shared with me
                            </span>
                        ) : (
                            <>
                                {/* <span className="inline-flex items-center ml-2 gap-1 text-[10px] font-medium text-white bg-slate-900 px-1.5 py-0.5 rounded shrink-0"></span> */}
                                <span className="inline-flex items-center ml-2 gap-1 text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">
                                    <span className="material-symbols-outlined text-[12px]">shield_person</span>
                                    Owner
                                </span>
                                {model.isShared && (
                                    <span className="inline-flex items-center ml-1 gap-1 text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded shrink-0">
                                        <span className="material-symbols-outlined text-[12px]">share</span>
                                        Shared
                                    </span>
                                )}
                            </>
                        )}
                        <div className="flex-grow"/>
                        {
                            model.editing ? <div></div> :
                            <div className="models-item-component-icons flex">
                                <div className="relative group/fin">
                                    <button
                                        className={cn(
                                            "size-8 rounded-lg transition-colors flex items-center justify-center",
                                            !model.isNew && model.modelRulesDeducted?.is_active !== false
                                                ? "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                                                : "opacity-50 cursor-not-allowed text-slate-400"
                                        )}
                                        disabled={model.modelRulesDeducted?.is_active === false}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isSharedContext || userRights.right_to_read) {
                                                if (model.isNew) {
                                                    navigateToPage(SubPages.NewModel, model);
                                                }
                                                else {
                                                    navigateToPage(SubPages.FinancialModelEditor, model);
                                                }
                                            }
                                        }}
                                        aria-label="Open Financial Editor"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">monitoring</span>
                                    </button>
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-slate-800 text-white text-[11px] rounded opacity-0 group-hover/fin:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-10">
                                        {model.modelRulesDeducted?.is_active === false ? 'Plan expired' : 'Financial Editor'}
                                    </div>
                                </div>
                                <div className="relative group/ope">
                                    <button
                                        className={cn(
                                            "size-8 rounded-lg transition-colors flex items-center justify-center",
                                            !model.isNew && model.modelRulesDeducted?.bpo_access && model.modelRulesDeducted?.is_active
                                                ? "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                                                : "opacity-50 cursor-not-allowed text-slate-400"
                                        )}
                                        disabled={model.isNew || !model.modelRulesDeducted?.bpo_access || !model.modelRulesDeducted?.is_active}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!model.isNew) {
                                                navigateToPage(SubPages.OperationalModelEditor, model);
                                            }
                                        }}
                                        aria-label="Open Operational Editor"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">analytics</span>
                                    </button>
                                    <div className="absolute bottom-full right-0 mb-1.5 px-2 py-1 bg-slate-800 text-white text-[11px] rounded opacity-0 group-hover/ope:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-10">
                                        {!model.modelRulesDeducted?.is_active ? 'Plan expired' : !model.modelRulesDeducted?.bpo_access ? 'Operational (Upgrade required)' : 'Operational Editor'}
                                    </div>
                                </div>
                                {!isSharedContext && (
                                    <button
                                        className={cn(
                                            "size-8 rounded-lg transition-colors flex items-center justify-center",
                                            model.modelRulesDeducted?.is_active
                                                ? "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                                                : "opacity-50 cursor-not-allowed text-slate-400"
                                        )}
                                        disabled={!model.modelRulesDeducted?.is_active}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            model.editing = true;
                                            forceRender();
                                        }}
                                        aria-label="Rename model"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">drive_file_rename_outline</span>
                                    </button>
                                )}
                            </div>
                        }
                        <button
                            className="size-8 mr-2 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-center text-slate-500 hover:text-slate-700"
                            onClick={(e) => {
                                e.stopPropagation();
                                setMenuAnchorEl(e.currentTarget);
                            }}
                            aria-label="More options"
                        >
                            <span className="material-symbols-outlined text-[20px]">more_vert</span>
                        </button>
                        <ModelListItemOptionMenu
                            model={model}
                            reloadState={reloadState}
                            state={state}
                            isSharedContext={isSharedContext}
                            userRights={isSharedContext ? userRights : undefined}
                            stateMyModels={stateMyModels}
                            anchorEl={menuAnchorEl}
                            onClose={() => setMenuAnchorEl(null)}
                        />
                    </div>
                </div>
                {isSharedContext
                    ? leaveModal(model, state, reloadState)
                    : deleteModal(model, state, reloadState)
                }
            </div>
        </div>
    )
}
